import { and } from 'ember-truth-helpers'
import { array } from '@ember/helper'
import { ContentLevelSelector } from 'teacher-ui-client/components/reporting/content-level-selector'
import { exportFilename, toRequestScope } from 'teacher-ui-client/services/reporting'
import {
  InlineAlert,
  AlertType,
  InlineAlertSubtitle,
  InlineAlertTitle,
} from 'teacher-ui-client/components/inline-alert'
import {
  isUnsupportedStandardsSet,
  StandardsSetUnsupportedAlert,
} from 'teacher-ui-client/components/standards-set/unsupported-alert'
import { keepLatest } from 'reactiveweb/keep-latest'
import { NoReportValue } from 'teacher-ui-client/components/reporting/no-report-value'
import { NoStandardsSetAlert } from 'teacher-ui-client/components/standards-set/no-standards-set-alert'
import { PerformanceOverview } from 'teacher-ui-client/components/reporting/performance-overview'
import { Product } from 'teacher-ui-client/utils/products'
import { ReportingControls } from 'teacher-ui-client/components/reporting/controls'
import { ReportingControlsGap } from 'teacher-ui-client/components/reporting/controls-gap'
import { ReportingPeriodSelect } from 'teacher-ui-client/components/reporting/period-select'
import { ReportingPrintButton } from 'teacher-ui-client/components/reporting/print-button'
import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { ReportingUiScopeSelect } from 'teacher-ui-client/components/reporting/ui-scope-select'
import { service } from '@ember/service'
import {
  StandardsOverview,
  type StandardsOverviewReportData,
} from 'teacher-ui-client/components/reporting/standards-performance/overview'
import {
  StandardsResults,
  type StandardsResultsReportData,
} from 'teacher-ui-client/components/reporting/standards-performance/results'
import { StandardsSetUpdatedAlert } from 'teacher-ui-client/components/standards-set/updated-alert'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { t } from 'ember-intl'
import { task } from 'ember-concurrency'
import { trackedFunction } from 'reactiveweb/function'
import { use } from 'ember-resources'
import Component from '@glimmer/component'
import RouteTemplate from 'ember-route-template'
import StudentSearchComponent from 'teacher-ui-client/components/student-search'
import type { RexStandardsPerformanceController } from 'teacher-ui-client/reporting/ui-scope/rex/standards-performance/controller'
import type { RexStandardsPerformanceRoute } from 'teacher-ui-client/reporting/ui-scope/rex/standards-performance/route'
import type { TOC } from '@ember/component/template-only'
import type ReportingService from 'teacher-ui-client/services/reporting'
import type SessionService from 'teacher-ui-client/services/session'

interface Signature {
  Args: {
    model: ModelFor<RexStandardsPerformanceRoute>
    controller: RexStandardsPerformanceController
  }
}

export class RexStandardsPerformancePage extends Component<Signature> {
  @service reporting!: ReportingService
  @service session!: SessionService

  fetchOverviewReport = trackedFunction(this, () => {
    const requestScope = toRequestScope(this.args.model.uiScope)
    const url = this.reporting.buildGravityUrl({
      product: Product.REX,
      requestScope,
      reportName: 'standards-performance/overview',
    })
    const query = this.reporting.buildQuery({
      period: this.args.model.period,
      contentLevel: this.args.controller.contentLevel,
    })
    return this.reporting.fetchReport<StandardsOverviewReportData>(url, query)
  })

  fetchResultsReport = trackedFunction(this, () => {
    const requestScope = toRequestScope(this.args.model.uiScope)
    const url = this.reporting.buildGravityUrl({
      product: Product.REX,
      requestScope,
      reportName: 'standards-performance/results',
    })
    const query = this.reporting.buildQuery({
      period: this.args.model.period,
      page: { number: this.args.controller.pageNumber, size: this.args.controller.perPage },
      contentLevel: this.args.controller.contentLevel,
      sort: this.args.controller.sort,
    })
    return this.reporting.fetchReport<StandardsResultsReportData>(url, query)
  })

  @use
  latestOverviewReport = keepLatest({
    value: () => this.fetchOverviewReport.value,
    when: () => this.fetchOverviewReport.isPending,
  })

  @use
  latestResultsReport = keepLatest({
    value: () => this.fetchResultsReport.value,
    when: () => this.fetchResultsReport.isPending,
  })

  exportResultsCsv = task({ drop: true }, async () => {
    const requestScope = toRequestScope(this.args.model.uiScope)
    const url = this.reporting.buildGravityUrl({
      product: Product.REX,
      requestScope,
      reportName: 'standards-performance/results',
    })
    const query = this.reporting.buildQuery({
      period: this.args.model.period,
      contentLevel: this.args.controller.contentLevel,
      sort: this.args.controller.sort,
    })
    const filename = exportFilename(Product.REX, 'standards-performance', '.csv')

    await this.reporting.exportReportCsv(url, query, filename)
  })

  get latestResultsReportData() {
    return this.latestResultsReport?.results.length ? this.latestResultsReport : undefined
  }

  get latestOverviewReportData() {
    return this.latestOverviewReport?.overview.total_lessons_completed ? this.latestOverviewReport : undefined
  }

  switchContentLevel = (value: string) => {
    this.args.controller.pageNumber = 1
    this.args.controller.contentLevel = value
  }

  // ReadingEggspress data comes in 6 content levels 1, 2, 3, 4, 5, 6
  get contentLevelPositions() {
    return [2, 3, 4, 5, 6, 7]
  }

  get isSubco() {
    return this.session.currentTeacher.subcoFor(SubscriptionType.Reading)
  }

  get isMissingStandards() {
    return !this.session.currentSchool.standardsSet
  }

  get isUnsupportedStandardsSet() {
    return this.session.currentSchool.standardsSet && isUnsupportedStandardsSet(this.session.currentSchool.standardsSet)
  }

  get isReportDisabled() {
    return this.isMissingStandards || this.isUnsupportedStandardsSet
  }

  get isOneRosterEnabled() {
    return Boolean(this.session.currentSchool.oneRosterEnabledAt)
  }

  <template>
    <ReportCoverageAlert @class="mt-3 mb-5" />
    <ReportingTeacherUiHeader @title={{t "reporting.pageTitles.standardsPerformance"}} />
    <ReportingControls>
      <ReportingUiScopeSelect class="shrink basis-80" @disabled={{this.isReportDisabled}} />
      <ReportingControlsGap />
      <ReportingPeriodSelect @disabled={{this.isReportDisabled}} />
      <ReportingPrintButton @disabled={{this.isReportDisabled}} />
      <ReportingControlsGap />
      <StudentSearchComponent
        @studentRouteName="reporting.ui-scope.rex.student-summary"
        @students={{@model.scopedStudents}}
        @includeSelectAll={{false}}
      />
    </ReportingControls>
    <div class="space-y-6">
      <ContentLevelSelector
        @switchContentLevel={{this.switchContentLevel}}
        @selectedContentLevel={{@controller.contentLevel}}
        @contentLevelPositions={{this.contentLevelPositions}}
        @useShortNames={{true}}
        @disabled={{this.isReportDisabled}}
      />
      {{#if this.isUnsupportedStandardsSet}}
        <StandardsSetUnsupportedAlert />
      {{else}}
        {{#if this.session.currentSchool.standardsSet}}
          <StandardsSetUpdatedAlert @report={{this.latestOverviewReport}} />
          {{#if (and this.latestOverviewReportData this.latestResultsReportData)}}
            {{#if this.latestOverviewReportData}}
              <StandardsOverview
                @reportData={{this.latestOverviewReportData}}
                @isReloading={{this.fetchOverviewReport.isPending}}
              />
              <PerformanceOverview
                @reportData={{this.latestOverviewReportData}}
                @showTitle={{true}}
                @isSubco={{this.isSubco}}
                @isReloading={{this.fetchOverviewReport.isPending}}
              />
            {{/if}}
            {{#if this.latestResultsReportData}}
              <StandardsResults
                @reportData={{this.latestResultsReportData}}
                @exportCsv={{this.exportResultsCsv}}
                @isReloading={{this.fetchResultsReport.isPending}}
                @product={{Product.REX}}
                @studentReportRoute="reporting.ui-scope.rex.lesson-performance"
              />
            {{/if}}
          {{else}}
            <NoReportValue @reportStates={{array this.fetchResultsReport this.fetchOverviewReport}} />
          {{/if}}
        {{else}}
          <NoStandardsSetAlert
            @hasDisco={{Boolean this.session.currentSchool.district.districtCoordinators.length}}
            @isSubco={{this.isSubco}}
            @isOneRosterEnabled={{this.isOneRosterEnabled}}
          />
        {{/if}}
      {{/if}}
    </div>
  </template>
}

interface AlertSignature {
  Element: HTMLDivElement
  Args: {
    class: string
  }
}

export const ReportCoverageAlert: TOC<AlertSignature> = <template>
  <div class={{@class}}>
    <InlineAlert data-test-report-coverage-alert @type={{AlertType.Critical}}>
      <InlineAlertTitle>
        {{t "reporting.standardsPerformance.reportCoverageAlert.title"}}
      </InlineAlertTitle>
      <InlineAlertSubtitle class="max-w-[750px]">
        {{t "reporting.standardsPerformance.reportCoverageAlert.subTitle" htmlSafe=true}}
      </InlineAlertSubtitle>
    </InlineAlert>
  </div>
</template>

export default RouteTemplate(RexStandardsPerformancePage)
